import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  Grid,
} from "@mui/material";
import {
  CloudUpload,
  Close,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import axios from "axios";
import { useSearchParams, useNavigate } from "react-router-dom";
import { decryptAppId, decryptText, encryptAppId, encryptText } from "../../../utils/encryption";
import FileUploadComponent from "../../../components/FileUploadComponent";
import s3Utils from "../../../utils/s3Utils";
import acProcessService from '../../../services/acProcessService';


const Advisory = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get encrypted parameters from URL
  const encryptedAppId = searchParams.get("appId");
  const encryptedOrgId = searchParams.get("orgId");
  const encryptedLabTypeId = searchParams.get("labTypeId") || "3";

  // Decrypt parameters
  const appIdFromUrl = encryptedAppId ? decryptAppId(encryptedAppId) : null;
  const orgIdFromUrl = encryptedOrgId ? decryptText(encryptedOrgId) : null;
  const labTypeIdFromUrl = decryptText(encryptedLabTypeId);


  const [appId, setAppId] = useState(appIdFromUrl);
  const [orgId, setOrgId] = useState(orgIdFromUrl);
  const [labTypeId, setLabTypeId] = useState(labTypeIdFromUrl);
  // Advisory data
  const [advisoryList, setAdvisoryList] = useState([]);
  const [currentAdvisory, setCurrentAdvisory] = useState(null);
  const [application, setApplication] = useState(null);
  
  // Modal states
  const [openCabReplyModal, setOpenCabReplyModal] = useState(false);
  const [openDoReplyModal, setOpenDoReplyModal] = useState(false);
  const [openCloseModal, setOpenCloseModal] = useState(false);
  const [openUpdateCabModal, setOpenUpdateCabModal] = useState(false);
  const [openUpdateDoModal, setOpenUpdateDoModal] = useState(false);
  const [openUpdateCloseModal, setOpenUpdateCloseModal] = useState(false);
  const [openReplyTrailModal, setOpenReplyTrailModal] = useState(false);
  const [replyTrailData, setReplyTrailData] = useState([]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  // Send to CAB states
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState("");

  // Form fields
  const [cabRemark, setCabRemark] = useState("");
  const [doRemark, setDoRemark] = useState("");
  const [closingRemark, setClosingRemark] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState("");

  // File upload from S3
  const [uploadedAdvisoryFile, setUploadedAdvisoryFile] = useState(null);
  const [fileUploadKey, setFileUploadKey] = useState(0);

  // Validation errors
  const [errors, setErrors] = useState({});
  const [fileError, setFileError] = useState("");
  const [loading, setLoading] = useState(false);
  const [updatingStage, setUpdatingStage] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Fetch advisory data on component mount
  useEffect(() => {
    if (appId) {
      fetchAdvisoryData();
    }
  }, [appId]);

  // Fetch advisory data from API
  const fetchAdvisoryData = async () => {
    try {
      setLoading(true);
      const appResponse = await acProcessService.getApplicationDetails(appId);
      const appData = appResponse.data;
      setApplication(appData);
      const response = await axios.get(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetAdvisroyreply`,
        {
          params: {
            appid: appId,
            usertypeid: 4,
          },
        },
      );

      if (response.data && response.data.isstatus && response.data.advisry) {
        setAdvisoryList(response.data.advisry);
      } else {
        setAdvisoryList([]);
      }
    } catch (error) {
      console.error("Error fetching advisory data:", error);
      setSnackbar({
        open: true,
        message: "Error loading advisory data. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Check if all advisories are closed
  const areAllAdvisoriesClosed = () => {
    if (advisoryList.length === 0) return false;
    return advisoryList.every(item => item.status === "Closed");
  };

  // Handle update stage to 13
  const handleUpdateStageTo13 = async () => {
    try {
      setUpdatingStage(true);
      await acProcessService.updateApplicationStage(appId, "7");
      
      setSnackbar({
        open: true,
        message: "Status updated successfully!",
        severity: "success",
      });
      
      // Refresh application data to reflect new stage
      await fetchAdvisoryData();
      
    } catch (error) {
      console.error("Error updating stage:", error);
      setSnackbar({
        open: true,
        message: "Error updating stage. Please try again.",
        severity: "error",
      });
    } finally {
      setUpdatingStage(false);
      setOpenConfirmDialog(false);
    }
  };

  // Handle confirmation dialog open
  const handleOpenConfirmDialog = () => {
    setOpenConfirmDialog(true);
  };

  const handleFileOpen = async (fileUrl) => {
    if (!fileUrl) {
      setErrors("No file available to view");
      return;
    }

    try {
      setLoading(true);
      // Use s3Utils to handle file opening with signed URLs
      await s3Utils.handleFileViewWithS3(fileUrl, {
        showError: (msg) => setErrors(msg),
        showLoading: () => setLoading(true),
        hideLoading: () => setLoading(false),
        expiresIn: 172800, // 48 hours
      });
    } catch (err) {
      setErrors("Failed to open file");
      console.error("Error opening file:", err);
    } finally {
      setLoading(false);
    }
  };

  // Handle file upload from FileUploadComponent
  const handleFileUploadChange = (fieldName, fileData) => {
    if (fileData) {
      setUploadedAdvisoryFile(fileData);
      setUploadedFileName(fileData.fileName || "");
      setSnackbar({
        open: true,
        message: "File uploaded successfully",
        severity: "success",
      });
    } else {
      setUploadedAdvisoryFile(null);
      setUploadedFileName("");
    }
  };

  // Clear file upload
  const handleClearFile = () => {
    setUploadedFile(null);
    setUploadedFileName("");
    setUploadedAdvisoryFile(null);
    setFileError("");
    // Force remount of FileUploadComponent
    setFileUploadKey((prev) => prev + 1);
  };

  // Validate CAB Reply
  const validateCabReply = () => {
    const newErrors = {};

    if (!cabRemark.trim()) {
      newErrors.cabRemark = "Enter Remarks Required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate DO Reply
  const validateDoReply = () => {
    const newErrors = {};

    if (!doRemark.trim()) {
      newErrors.doRemark = "Enter Remarks Required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate Close
  const validateClose = () => {
    const newErrors = {};

    if (!closingRemark.trim()) {
      newErrors.closingRemark = "Enter Remarks Required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Reset form data
  const resetForm = (type) => {
    if (type === "cab") {
      setCabRemark("");
      handleClearFile();
    } else if (type === "do") {
      setDoRemark("");
    } else if (type === "close") {
      setClosingRemark("");
    }
    setErrors({});
    setFileError("");
    setFileUploadKey((prev) => prev + 1);
  };

  // Handle CAB Reply Submit
  const handleCabReplySubmit = async () => {
    if (!validateCabReply()) {
      return;
    }

    if (!currentAdvisory) {
      setSnackbar({
        open: true,
        message: "No advisory selected",
        severity: "error",
      });
      return;
    }

    setLoading(true);

    try {
      // Prepare payload (CAB Reply - when viscab=1, DO officer replies as CAB)
      const payload = {
        appid: appId?.toString() || "",
        id: currentAdvisory.id?.toString() || "",
        labtypeid: labTypeId,
        fileupload: uploadedAdvisoryFile?.fileName || uploadedFileName || "",
        replyby: "CAB",
        reply: cabRemark.trim(),
        updatedby: localStorage.getItem("orgId") || "0",
      };

      // Make API call
      const response = await axios.post(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Advisroyreply`,
        payload,
      );

      // Show success message
      setSnackbar({
        open: true,
        message: "Reply submitted successfully.",
        severity: "success",
      });
      // Reset form and close modal
      setTimeout(() => {
        resetForm("cab");
        setOpenCabReplyModal(false);
        setCurrentAdvisory(null);
        // Refresh advisory data
        fetchAdvisoryData();
      }, 1000);
    } catch (error) {
      console.error("Error submitting CAB reply:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Error submitting reply. Please try again.";

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle DO Reply Submit
  const handleDoReplySubmit = async () => {
    if (!validateDoReply()) {
      return;
    }

    if (!currentAdvisory) {
      setSnackbar({
        open: true,
        message: "No advisory selected",
        severity: "error",
      });
      return;
    }

    setLoading(true);

    try {
      const payload = {
        appid: appId?.toString() || "",
        id: currentAdvisory.id?.toString() || "",
        labtypeid: labTypeId,
        replyby: "DO",
        reply: doRemark.trim(),
        updatedby: localStorage.getItem("orgId") || "0",
      };

      const response = await axios.post(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Advisroyreply`,
        payload,
      );

      setSnackbar({
        open: true,
        message: "Reply submitted successfully.",
        severity: "success",
      });

      setTimeout(() => {
        resetForm("do");
        setOpenDoReplyModal(false);
        setCurrentAdvisory(null);
        fetchAdvisoryData();
      }, 1000);
    } catch (error) {
      console.error("Error submitting DO reply:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Error submitting reply. Please try again.";

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle Close Submit
  const handleCloseSubmit = async () => {
    if (!validateClose()) {
      return;
    }

    if (!currentAdvisory) {
      setSnackbar({
        open: true,
        message: "No advisory selected",
        severity: "error",
      });
      return;
    }

    setLoading(true);

    try {
      const payload = {
        appid: appId?.toString() || "",
        id: currentAdvisory.id?.toString() || "",
        nCid: currentAdvisory.nCid?.toString() || "",
        labTypeId: labTypeId,
        replyBy: "DO",
        closeRemark: closingRemark.trim(),
        updatedBy: localStorage.getItem("orgId") || "0",
      };

      const response = await axios.post(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/CloseAdvisory`,
        payload,
      );

      setSnackbar({
        open: true,
        message: "Advisory closed successfully.",
        severity: "success",
      });

      setTimeout(() => {
        resetForm("close");
        setOpenCloseModal(false);
        setCurrentAdvisory(null);
        fetchAdvisoryData();
      }, 1000);
    } catch (error) {
      console.error("Error closing advisory:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Error closing advisory. Please try again.";

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle Update CAB Reply
  const handleUpdateCabReply = async () => {
    if (!validateCabReply()) {
      return;
    }

    if (!currentAdvisory) {
      setSnackbar({
        open: true,
        message: "No advisory selected",
        severity: "error",
      });
      return;
    }

    setLoading(true);

    try {
      let fileUploadPath = currentAdvisory.fileUpload || "";
      
      if (uploadedAdvisoryFile?.fileName) {
        fileUploadPath = uploadedAdvisoryFile.fileName;
      }

      const payload = {
        appid: appId?.toString() || "",
        id: currentAdvisory.id?.toString() || "",
        labtypeid: labTypeId,
        replyby: "CAB",
        reply: cabRemark.trim(),
        updatedby: localStorage.getItem("orgId") || "0",
      };

      const response = await axios.post(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Advisroyreply`,
        payload,
      );

      setSnackbar({
        open: true,
        message: "Reply updated successfully.",
        severity: "success",
      });

      setTimeout(() => {
        resetForm("cab");
        setOpenUpdateCabModal(false);
        setCurrentAdvisory(null);
        fetchAdvisoryData();
      }, 1000);
    } catch (error) {
      console.error("Error updating CAB reply:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Error updating reply. Please try again.";

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle Update DO Reply
  const handleUpdateDoReply = async () => {
    if (!validateDoReply()) {
      return;
    }

    if (!currentAdvisory) {
      setSnackbar({
        open: true,
        message: "No advisory selected",
        severity: "error",
      });
      return;
    }

    setLoading(true);

    try {
      const payload = {
        appid: appId?.toString() || "",
        id: currentAdvisory.id?.toString() || "",
        labtypeid: labTypeId,
        replyby: "DO",
        reply: doRemark.trim(),
        updatedby: localStorage.getItem("orgId") || "0",
      };

      const response = await axios.post(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Advisroyreply`,
        payload,
      );

      setSnackbar({
        open: true,
        message: "Reply updated successfully.",
        severity: "success",
      });

      setTimeout(() => {
        resetForm("do");
        setOpenUpdateDoModal(false);
        setCurrentAdvisory(null);
        fetchAdvisoryData();
      }, 1000);
    } catch (error) {
      console.error("Error updating DO reply:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Error updating reply. Please try again.";

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle Update Close Reply
  const handleUpdateCloseReply = async () => {
    if (!validateClose()) {
      return;
    }

    if (!currentAdvisory) {
      setSnackbar({
        open: true,
        message: "No advisory selected",
        severity: "error",
      });
      return;
    }

    setLoading(true);

    try {
      const payload = {
        appid: appId?.toString() || "",
        id: currentAdvisory.id?.toString() || "",
        nCid: currentAdvisory.nCid?.toString() || "",
        labTypeId: labTypeId,
        replyBy: "DO",
        closeRemark: closingRemark.trim(),
        updatedBy: localStorage.getItem("orgId") || "0",
      };

      const response = await axios.post(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/CloseAdvisory`,
        payload,
      );

      setSnackbar({
        open: true,
        message: "Close reply updated successfully.",
        severity: "success",
      });

      setTimeout(() => {
        resetForm("close");
        setOpenUpdateCloseModal(false);
        setCurrentAdvisory(null);
        fetchAdvisoryData();
      }, 1000);
    } catch (error) {
      console.error("Error updating close reply:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Error updating close reply. Please try again.";

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch Reply Trail
  const fetchReplyTrail = async (advisoryId) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/getadivsoryreplyby?id=${advisoryId}&appid=${appId}&usertypeid=4` );

      console.log('Reply trail response:', response.data);

      if (response.data && response.data.advisry && response.data.advisry.length > 0) {
        setReplyTrailData(response.data.advisry || []);
        setOpenReplyTrailModal(true);
      } else {
        setSnackbar({
          open: true,
          message: "No reply trail found.",
          severity: "info",
        });
      }
    } catch (error) {
      console.error("Error fetching reply trail:", error);
      setSnackbar({
        open: true,
        message: "Error loading reply trail. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Check if Send to CAB button should be visible
  // Button shows when all advisories have been replied to but NOT all are closed
  const checkAllReplied = () => {
    if (advisoryList.length === 0) return false;
    
    // Check if all are either "pending" or "Closed" (no items waiting for DO reply)
    const allProcessed = advisoryList.every(item => {
      return item.status === "pending" || item.status === "Closed";
    });
    
    // Check if at least one is still "pending" (not all closed)
    const hasNonClosed = advisoryList.some(item => item.status === "pending");
    
    // Show button only if all processed AND at least one is not closed
    return allProcessed && hasNonClosed;
  };

  // Handle Send to CAB button click
  const handleSendToCAB = () => {
    setPasswordInput("");
    setPasswordError("");
    setOpenPasswordModal(true);
  };

  // Verify password and update stage
  const handlePasswordVerify = async () => {
    if (!passwordInput.trim()) {
      setPasswordError("Password is required");
      return;
    }

    setLoading(true);
    try {
      // Verify password first
      const passwordResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/verify-password`,
        {
          orgId: localStorage.getItem('orgId') || '0',
          password: passwordInput
        }
      );
      
      if (!passwordResponse.data.success) {
        setPasswordError('Invalid password');
        setLoading(false);
        return;
      }
      await acProcessService.updateApplicationStage(appId, '12b');
        setSnackbar({
          open: true,
          message: "Application sent to CAB successfully!.",
          severity: "success",
        });
        setOpenPasswordModal(false);
        setPasswordInput("");
        setPasswordError("");
        
        // Refresh advisory data
        setTimeout(() => {
          fetchAdvisoryData();
        }, 1500);

    } catch (error) {
      console.error("Error in send to CAB process:", error);
      setSnackbar({
        open: true,
        message: error.message || "Error sending to CAB. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close
  const handleModalClose = (type) => {
    if (type === "cab") {
      setOpenCabReplyModal(false);
      resetForm("cab");
    } else if (type === "do") {
      setOpenDoReplyModal(false);
      resetForm("do");
    } else if (type === "close") {
      setOpenCloseModal(false);
      resetForm("close");
    } else if (type === "updateCab") {
      setOpenUpdateCabModal(false);
      resetForm("cab");
    } else if (type === "updateDo") {
      setOpenUpdateDoModal(false);
      resetForm("do");
    } else if (type === "updateClose") {
      setOpenUpdateCloseModal(false);
      resetForm("close");
    } else if (type === "replyTrail") {
      setOpenReplyTrailModal(false);
      setReplyTrailData([]);
    }
  };

  // Render buttons based on reply type
  const renderReplyButton = (item) => {
    const viscab = parseInt(item.viscab);
    const viscabUpdate = parseInt(item.viscabUpdate);
    const visReplyDo = parseInt(item.visReplyDo);
    const visCloseDo = parseInt(item.visCloseDo);
    const visDoReplyUpdate = parseInt(item.visDoReplyUpdate);
    const visDoCloseUpdate = parseInt(item.visDoCloseUpdate);
    const visReplyTrail = parseInt(item.visReplyTrail);

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {viscab === 1 && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              setCurrentAdvisory(item);
              setCabRemark("");
              setOpenCabReplyModal(true);
            }}
            disabled={loading}
          >
            Reply
          </Button>
        )}
        {viscabUpdate === 1 && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              setCurrentAdvisory(item);
              setCabRemark(item.reply || "");
              setOpenUpdateCabModal(true);
            }}
            disabled={loading}
          >
            Update Reply
          </Button>
        )}
        {(visReplyDo === 1 && item.status !== "Closed") && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              setCurrentAdvisory(item);
              setDoRemark("");
              setOpenDoReplyModal(true);
            }}
            disabled={loading}
          >
            Reply (DO)
          </Button>
        )}
        {visDoReplyUpdate === 1 && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              setCurrentAdvisory(item);
              setDoRemark(item.doReply || "");
              setOpenUpdateDoModal(true);
            }}
            disabled={loading}
          >
            Update Reply
          </Button>
        )}
        {visCloseDo === 1 && (
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={() => {
              setCurrentAdvisory(item);
              setClosingRemark("");
              setOpenCloseModal(true);
            }}
            disabled={loading}
          >
            Close
          </Button>
        )}
        {(visDoCloseUpdate === 1 && item.status !== "Closed") && (
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={() => {
              setCurrentAdvisory(item);
              setClosingRemark(item.doReply || "");
              setOpenUpdateCloseModal(true);
            }}
            disabled={loading}
          >
            Update Close Reply
          </Button>
        )}
        {visReplyTrail === 1 && (
          <Button
            variant="contained"
            color="info"
            size="small"
            onClick={() => {
              fetchReplyTrail(item.id);
            }}
            disabled={loading}
          >
            Reply Trail
          </Button>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3, mt: 1 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">
            Advisory Reply Management
          </Typography>
           {application?.Stage === '7g' && (
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={handleSendToCAB}
              disabled={loading}
              sx={{ 
                fontWeight: 'bold',
                px: 4,
                py: 1.5,
                boxShadow: 3
              }}
            >
              Send to CAB
            </Button>
          )}
          {checkAllReplied() && ( application?.Stage === '12' || application?.Stage === '12a') && (
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={handleSendToCAB}
              disabled={loading}
              sx={{ 
                fontWeight: 'bold',
                px: 4,
                py: 1.5,
                boxShadow: 3
              }}
            >
              Send to CAB
            </Button>
          )}
        </Box>

        {loading && advisoryList.length === 0 ? (
          <Typography>Loading advisory data...</Typography>
        ) : advisoryList.length === 0 ? (
          <Typography>No advisory data found.</Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>S.No.</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>CAB Reply/Date</TableCell>
                  <TableCell>Evidence</TableCell>
                  <TableCell>DO Reply</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {advisoryList.map((item, index) => (
                  <TableRow key={item.id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{item.description}</TableCell>
                    <TableCell>
                      {item.reply || "N/A"}{item.title ? ` ( ${item.title} )` : ""}
                    </TableCell>
                    <TableCell>
                      {item.fileUpload && parseInt(item.visFile) === 1 ? (
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleFileOpen(item.fileUpload)}
                          disabled={loading}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      ) : (
                        "N/A"
                      )}
                    </TableCell>
                    <TableCell>{item.doReply || "N/A"}</TableCell>
                    <TableCell>
                      <Typography sx={{ color: item.statusClr }}>
                        {item.status || "Pending"}
                      </Typography>
                    </TableCell>
                    <TableCell>{renderReplyButton(item)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        
        {/* Update Stage Button - Shows when all advisories are closed */}
        {advisoryList.length > 0 && areAllAdvisoriesClosed() && application.Stage ==='12b' && (
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleOpenConfirmDialog}
              disabled={updatingStage}
              sx={{
                fontWeight: 'bold',
                px: 5,
                py: 1.5,
                fontSize: '1rem',
                boxShadow: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  boxShadow: 6,
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {updatingStage ? 'Updating...' : 'Mark Advisory Process completed'}
            </Button>
          </Box>
        )}
      </Paper>

      {/* CAB Reply Modal */}
      <Dialog
        open={openCabReplyModal}
        onClose={() => setOpenCabReplyModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Send Reply To NABL</Typography>
            <IconButton
              onClick={() => setOpenCabReplyModal(false)}
              size="small"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Enter Remarks
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={cabRemark}
              onChange={(e) => setCabRemark(e.target.value)}
              error={!!errors.cabRemark}
              helperText={errors.cabRemark}
              required
            />
          </Box>

          <Box sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FileUploadComponent
                  key={`advisoryFile-${fileUploadKey}`}
                  fieldName="advisoryFile"
                  label="Upload Evidence File"
                  orgId={appId?.toString() || "advisory"}
                  section="advisory-documents"
                  accept=".doc,.docx,.pdf,.png,.jpg,.gif,.txt,.xls,.xlsx,.mp4"
                  maxSize={10}
                  onChange={handleFileUploadChange}
                  initialFile={uploadedAdvisoryFile}
                  localDeleteOnly={false}
                  required={false}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCabReplySubmit}
            variant="contained"
            disabled={loading}
          >
            {loading ? "Submitting..." : "Submit"}
          </Button>
          <Button
            onClick={() => setOpenCabReplyModal(false)}
            color="error"
            disabled={loading}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* DO Reply Modal */}
      <Dialog
        open={openDoReplyModal}
        onClose={() => setOpenDoReplyModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Send Reply To CAB</Typography>
            <IconButton onClick={() => setOpenDoReplyModal(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Enter Remarks
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={doRemark}
              onChange={(e) => setDoRemark(e.target.value)}
              error={!!errors.doRemark}
              helperText={errors.doRemark}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDoReplySubmit} variant="contained">
            Submit
          </Button>
          <Button onClick={() => setOpenDoReplyModal(false)} color="error">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Close Advisory Modal */}
      <Dialog
        open={openCloseModal}
        onClose={() => setOpenCloseModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Close Advisory</Typography>
            <IconButton onClick={() => setOpenCloseModal(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Enter Remarks
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={closingRemark}
              onChange={(e) => setClosingRemark(e.target.value)}
              error={!!errors.closingRemark}
              helperText={errors.closingRemark}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSubmit} variant="contained" disabled={loading}>
            {loading ? "Submitting..." : "Submit"}
          </Button>
          <Button onClick={() => setOpenCloseModal(false)} color="error" disabled={loading}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update CAB Reply Modal */}
      <Dialog
        open={openUpdateCabModal}
        onClose={() => handleModalClose("updateCab")}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Update Reply to NABL</Typography>
            <IconButton onClick={() => handleModalClose("updateCab")} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Enter Corrective Action
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={cabRemark}
              onChange={(e) => setCabRemark(e.target.value)}
              error={!!errors.cabRemark}
              helperText={errors.cabRemark}
              required
            />
          </Box>

          <Box sx={{ mt: 3 }}>
            {currentAdvisory?.fileUpload && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Current Evidence:
                </Typography>
                <Link
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleFileOpen(currentAdvisory.fileUpload);
                  }}
                  sx={{ mr: 2 }}
                >
                  Download
                </Link>
                <Button
                  size="small"
                  color="error"
                  onClick={() => {
                    setCurrentAdvisory({ ...currentAdvisory, fileUpload: "" });
                  }}
                >
                  Remove
                </Button>
              </Box>
            )}
            
            {!currentAdvisory?.fileUpload && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FileUploadComponent
                    key={`updateAdvisoryFile-${fileUploadKey}`}
                    fieldName="updateAdvisoryFile"
                    label="Upload Evidence File"
                    orgId={appId?.toString() || "advisory"}
                    section="advisory-documents"
                    accept=".doc,.docx,.pdf,.png,.jpg,.gif,.txt,.xls,.xlsx,.mp4"
                    maxSize={10}
                    onChange={handleFileUploadChange}
                    initialFile={uploadedAdvisoryFile}
                    localDeleteOnly={false}
                    required={false}
                  />
                </Grid>
              </Grid>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleUpdateCabReply}
            variant="contained"
            disabled={loading}
          >
            {loading ? "Updating..." : "Submit"}
          </Button>
          <Button
            onClick={() => handleModalClose("updateCab")}
            color="error"
            disabled={loading}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update DO Reply Modal */}
      <Dialog
        open={openUpdateDoModal}
        onClose={() => handleModalClose("updateDo")}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Update Reply to CAB</Typography>
            <IconButton onClick={() => handleModalClose("updateDo")} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Enter Remarks
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={doRemark}
              onChange={(e) => setDoRemark(e.target.value)}
              error={!!errors.doRemark}
              helperText={errors.doRemark}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleUpdateDoReply}
            variant="contained"
            disabled={loading}
          >
            {loading ? "Updating..." : "Submit"}
          </Button>
          <Button
            onClick={() => handleModalClose("updateDo")}
            color="error"
            disabled={loading}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Close Reply Modal */}
      <Dialog
        open={openUpdateCloseModal}
        onClose={() => handleModalClose("updateClose")}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Update Close Remarks</Typography>
            <IconButton onClick={() => handleModalClose("updateClose")} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Enter Remarks
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={closingRemark}
              onChange={(e) => setClosingRemark(e.target.value)}
              error={!!errors.closingRemark}
              helperText={errors.closingRemark}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleUpdateCloseReply}
            variant="contained"
            disabled={loading}
          >
            {loading ? "Updating..." : "Submit"}
          </Button>
          <Button
            onClick={() => handleModalClose("updateClose")}
            color="error"
            disabled={loading}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reply Trail Modal */}
      <Dialog
        open={openReplyTrailModal}
        onClose={() => handleModalClose("replyTrail")}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Reply Trail between DO and CAB</Typography>
            <IconButton onClick={() => handleModalClose("replyTrail")} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>S.No.</TableCell>
                  <TableCell>Reply</TableCell>
                  <TableCell>Evidence</TableCell>
                  <TableCell>Reply By</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {replyTrailData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No reply trail found
                    </TableCell>
                  </TableRow>
                ) : (
                  replyTrailData.map((reply, index) => (
                    <TableRow key={reply.id || index}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{reply.reply || "N/A"}</TableCell>
                      <TableCell>
                        {reply.fileupload ? (
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleFileOpen(reply.fileupload)}
                            title="View Evidence"
                          >
                            <VisibilityIcon />
                          </IconButton>
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                      <TableCell>
                        {reply.replyBy === "CAB" ? (
                          <Box
                            sx={{
                              display: "inline-block",
                              px: 1,
                              py: 0.5,
                              bgcolor: "primary.light",
                              color: "primary.contrastText",
                              borderRadius: 1,
                            }}
                          >
                            CAB
                          </Box>
                        ) : reply.replyBy === "DO" ? (
                          <Box
                            sx={{
                              display: "inline-block",
                              px: 1,
                              py: 0.5,
                              bgcolor: "success.light",
                              color: "success.contrastText",
                              borderRadius: 1,
                            }}
                          >
                            DO
                          </Box>
                        ) : (
                          reply.replyBy || "N/A"
                        )}
                      </TableCell>
                      <TableCell>{reply.insertDAte || "N/A"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleModalClose("replyTrail")} color="primary">
            Close Trail
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Verification Modal for Send to CAB */}
      <Dialog
        open={openPasswordModal}
        onClose={() => setOpenPasswordModal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Verify Password</Typography>
            <IconButton onClick={() => setOpenPasswordModal(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This action will send the application to CAB.
          </Alert>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Enter Your Password to Confirm
            </Typography>
            <TextField
              fullWidth
              type="password"
              value={passwordInput}
              onChange={(e) => {
                setPasswordInput(e.target.value);
                setPasswordError("");
              }}
              error={!!passwordError}
              helperText={passwordError}
              placeholder="Enter password"
              autoFocus
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handlePasswordVerify}
            variant="contained"
            color="success"
            disabled={loading}
          >
            {loading ? "Verifying..." : "Confirm & Send to CAB"}
          </Button>
          <Button
            onClick={() => setOpenPasswordModal(false)}
            color="error"
            disabled={loading}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Mark Advisory Process Completed */}
      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Confirm Action</Typography>
            <IconButton onClick={() => setOpenConfirmDialog(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to mark the Advisory Process as completed? This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleUpdateStageTo13}
            variant="contained"
            color="success"
            disabled={updatingStage}
          >
            {updatingStage ? "Updating..." : "Confirm"}
          </Button>
          <Button
            onClick={() => setOpenConfirmDialog(false)}
            color="error"
            disabled={updatingStage}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Advisory;
